import JFTable from '../../component-lib/JFTable/JFTable';
import { currencyFormatter, dateFormat } from '../../components/Utils';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { ArchiveBoxXMarkIcon } from '@heroicons/react/16/solid';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../component-lib/catalyst/dropdown';
import StatusBadge from '../../component-lib/StatusBadge/StatusBadge';
import { Avatar } from '../../component-lib/catalyst/avatar';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import ConfirmDialog from '../../component-lib/ConfirmDialog/ConfirmDialog';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import archiveTask from '../../api-v2/mutations/archive-task';
import { getTaskStatus } from './index';

export default function TasksTable({
  forCurrentUser,
  data,
  onHeaderCellClick,
  orderedBy,
  orderDirection,
  refetchData,
}) {
  const navigate = useNavigate();
  const { tenant_locale, currency_symbol } = useTenantContext();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [taskToArchive, setTaskToArchive] = useState(null);

  const archiveTaskMutation = useMutation(
    archiveTask({
      onSuccess: () => {
        refetchData();
        setIsConfirmDialogOpen(false);
        setTaskToArchive(null);
      },
    }),
  );

  const columns = [
    {
      id: 'project',
      label: 'Project',
      cell: (row) => (
        <div className="overflow-hidden text-ellipsis text-nowrap">
          <Avatar
            src={row.project.buyerCompany.avatarUrl}
            initials={row.project.buyerCompany.name[0]}
            className="size-6 mr-2"
          />
          <Link
            title={row.project.name}
            to={`/projects/${row.project.id}`}
            className="font-bold text-blue-600 hover:underline"
          >
            {row.project.name}
          </Link>
        </div>
      ),
      width: 250,
    },
    {
      id: 'title',
      label: 'Title',
      cell: (row) => (
        <div className="overflow-hidden text-ellipsis text-nowrap">
          <Link
            title={row.title}
            to={`/projects/${row.project.id}/tasks/${row.id}`}
            className="font-bold text-blue-600 hover:underline"
            onClick={(event) => {
              event.preventDefault();
              navigate(`p/${row.project.id}/t/${row.id}`);
            }}
          >
            {row.title}
          </Link>
        </div>
      ),
      width: 250,
    },
    {
      id: 'startDate',
      label: 'Start date',
      align: 'center',
      cell: (row) => dateFormat(row.startDate),
    },
    {
      id: 'endDate',
      label: 'Due date',
      align: 'center',
      cell: (row) => dateFormat(row.actualEndDate || row.endDate),
    },
    ...(!forCurrentUser
      ? [
          {
            id: 'owner',
            label: 'Owner',
            align: 'center',
            cell: (row) => `${row.owner.firstName} ${row.owner.lastName}`,
          },
          {
            id: 'internalAssignee',
            label: 'Assignee',
            align: 'center',
            cell: (row) =>
              row.internalAssignee
                ? `${row.internalAssignee.firstName} ${row.internalAssignee.lastName}`
                : 'Unassigned',
          },
        ]
      : []),
    {
      id: 'externalAssignee',
      label: 'Contact',
      align: 'center',
      cell: (row) =>
        row.externalAssignee
          ? `${row.externalAssignee.firstName} ${row.externalAssignee.lastName}`
          : 'Unassigned',
    },
    {
      id: 'value',
      label: 'Project value',
      align: 'right',
      cell: (row) => currencyFormatter(tenant_locale, row.project.value, currency_symbol),
    },
    {
      id: 'priority',
      label: 'Priority',
      align: 'center',
      cell: (row) => row.project.priority,
    },
    {
      id: 'status',
      label: 'Status',
      align: 'center',
      cell: (row) => (
        <div>
          <StatusBadge status={getTaskStatus(row)} />
        </div>
      ),
    },
    {
      id: 'actions',
      label: '',
      cell: (row) => (
        <Dropdown>
          <DropdownButton plain>
            <EllipsisVerticalIcon />
          </DropdownButton>
          <DropdownMenu>
            <DropdownItem
              onClick={() => {
                setTaskToArchive(row.id);
                setIsConfirmDialogOpen(true);
              }}
            >
              <ArchiveBoxXMarkIcon />
              <DropdownLabel>Archive</DropdownLabel>
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <JFTable
        data={data}
        columns={columns}
        onHeaderCellClick={onHeaderCellClick}
        orderedBy={orderedBy}
        orderDirection={orderDirection}
      />
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        setIsOpen={() => {
          setTaskToArchive(null);
          setIsConfirmDialogOpen(false);
        }}
        title="Confirm archiving task"
        message="Are you sure you want to archive this task? Archived tasks cannot be restored."
        confirmText="Archive"
        confirmTextProgress="Archiving..."
        onConfirm={() => {
          archiveTaskMutation.mutate({ taskId: taskToArchive });
        }}
        onCancel={() => {
          setIsConfirmDialogOpen(false);
          setTaskToArchive(null);
        }}
        isPending={archiveTaskMutation.isPending}
      />
    </>
  );
}
